import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useFeatures } from '../contexts/FeatureContext';
import { authAPI } from '../services/api';
import { toast } from 'sonner';
import {
  LayoutDashboard, FileText, Receipt, Users, BookOpen, ArrowRightLeft,
  ChevronDown, ChevronRight, Settings, LogOut, Shield, TrendingUp,
  Wallet, BarChart3, Calculator, Landmark, ClipboardList, Truck,
  IndianRupee, FileCheck, UserCircle, Building2, KeyRound, X
} from 'lucide-react';

const navLink = "flex items-center gap-2.5 px-3 py-2 text-[13px] rounded-lg transition-all duration-200";
const activeClass = "bg-gradient-to-r from-purple-600 to-purple-500 text-white font-semibold shadow-md shadow-purple-200 dark:shadow-purple-900/30";
const inactiveClass = "text-gray-600 hover:bg-purple-50 hover:text-purple-700 dark:text-gray-400 dark:hover:bg-purple-900/20 dark:hover:text-purple-300";

function NavGroup({ label, icon: Icon, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div>
      <button onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-3 py-2 text-[10px] font-bold text-purple-400 uppercase tracking-[0.15em] hover:text-purple-600 dark:text-purple-500 dark:hover:text-purple-300 transition-colors duration-200"
        data-testid={`nav-group-${label.toLowerCase().replace(/\s/g, '-')}`}>
        <span className="flex items-center gap-2">
          {Icon && <Icon className="w-3.5 h-3.5" />}
          {label}
        </span>
        {open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
      </button>
      {open && <div className="space-y-0.5 ml-1">{children}</div>}
    </div>
  );
}

function NavItem({ to, icon: Icon, label, testId }) {
  return (
    <NavLink to={to} end={to === '/'} data-testid={testId}
      className={({ isActive }) => `${navLink} ${isActive ? activeClass : inactiveClass}`}>
      <Icon className="w-4 h-4 shrink-0" /><span>{label}</span>
    </NavLink>
  );
}

export default function Sidebar() {
  const { user, logout } = useAuth();
  const [showAccount, setShowAccount] = useState(false);
  const { hasFeature } = useFeatures();
  const navigate = useNavigate();
  const isAdmin = user?.role === 'admin';
  const isSuperAdmin = user?.role === 'super_admin';
  const f = (feat) => hasFeature(feat);

  const handleLogout = () => { logout(); navigate('/login'); };

  return (
    <aside className="w-56 bg-white/80 dark:bg-[hsl(260,15%,8%)] backdrop-blur-md border-r border-purple-100 dark:border-purple-900/30 flex flex-col h-screen sticky top-0 transition-colors duration-200" data-testid="sidebar">
      {/* Logo */}
      <div className="px-4 py-4 border-b border-purple-100 dark:border-purple-900/30">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-600 to-purple-400 flex items-center justify-center shadow-md shadow-purple-200 dark:shadow-purple-900/30">
            <span className="font-heading font-black text-white text-sm">T</span>
          </div>
          <div>
            <h1 className="font-heading font-bold text-sm text-gray-800 dark:text-gray-100 leading-tight tracking-tight">Transsolution</h1>
            <p className="text-[9px] text-purple-400 dark:text-purple-500 font-medium tracking-[0.1em] uppercase">ERP System</p>
          </div>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto px-2 py-3 space-y-1">
        {f('dashboard') && <NavItem to="/" icon={LayoutDashboard} label="Dashboard" testId="nav-dashboard" />}

        <NavGroup label="Accounting" icon={BookOpen}>
          {f('basic_accounting') && <NavItem to="/transactions" icon={ArrowRightLeft} label="Transactions" testId="nav-transactions" />}
          {f('basic_accounting') && <NavItem to="/voucher-entries" icon={ClipboardList} label="Journal Entries" testId="nav-journal" />}
          {f('basic_accounting') && <NavItem to="/chart-of-accounts" icon={BookOpen} label="Chart of Accounts" testId="nav-coa" />}
          {f('basic_accounting') && <NavItem to="/bank-accounts" icon={Building2} label="Bank Accounts" testId="nav-bank-accounts" />}
          {f('ledger') && <NavItem to="/ledger" icon={Landmark} label="Ledger" testId="nav-ledger" />}
          {f('trial_balance') && <NavItem to="/trial-balance" icon={Calculator} label="Trial Balance" testId="nav-tb" />}
          {f('profit_loss') && <NavItem to="/profit-loss" icon={TrendingUp} label="Profit & Loss" testId="nav-pl" />}
          {f('balance_sheet') && <NavItem to="/balance-sheet" icon={BarChart3} label="Balance Sheet" testId="nav-bs" />}
        </NavGroup>

        <NavGroup label="Sales" icon={FileText}>
          {f('invoices') && <NavItem to="/invoices" icon={FileText} label="Tax Invoice" testId="nav-invoices" />}
          {f('invoices') && <NavItem to="/advances" icon={Wallet} label="Advances" testId="nav-advances" />}
        </NavGroup>

        <NavGroup label="Transport" icon={Truck}>
          {f('receipts') && <NavItem to="/receipts" icon={Receipt} label="Lorry Receipt (LR)" testId="nav-receipts" />}
          {f('receipts') && <NavItem to="/collection-memo" icon={ClipboardList} label="Collection Memo" testId="nav-cm" />}
          {f('receipts') && <NavItem to="/vehicles" icon={Truck} label="Vehicles" testId="nav-vehicles" />}
          {f('receipts') && <NavItem to="/drivers" icon={UserCircle} label="Drivers" testId="nav-drivers" />}
          <NavItem to="/bill-books" icon={BookOpen} label="Bill Books" testId="nav-bill-books" />
          {f('route_management') && <NavItem to="/trips" icon={Truck} label="Trips" testId="nav-trips" />}
        </NavGroup>

        <NavGroup label="Reports" icon={BarChart3}>
          <NavItem to="/fleet-reports" icon={Truck} label="Fleet & Branch" testId="nav-fleet-reports" />
          {f('gst_reports') && <NavItem to="/gst-returns" icon={IndianRupee} label="GST Returns" testId="nav-gst-returns" />}
          {f('gst_reports') && <NavItem to="/tds-returns" icon={FileCheck} label="TDS Returns" testId="nav-tds-returns" />}
          {f('client_analytics') && <NavItem to="/party-analytics" icon={TrendingUp} label="Party Analytics" testId="nav-party-analytics" />}
          {f('invoices') && <NavItem to="/advance-summary" icon={Wallet} label="Advance Summary" testId="nav-adv-summary" />}
          {f('advanced_reports') && <NavItem to="/bank-reconciliation" icon={ArrowRightLeft} label="Reconciliation" testId="nav-bank-recon" />}
          {f('custom_reports') && <NavItem to="/report-builder" icon={BarChart3} label="Report Builder" testId="nav-report-builder" />}
        </NavGroup>

        <NavGroup label="Masters" icon={Users}>
          {f('parties') && <NavItem to="/parties" icon={Users} label="Parties" testId="nav-parties" />}
          {f('masters') && <NavItem to="/masters" icon={Settings} label="Masters" testId="nav-masters" />}
        </NavGroup>

        {(isAdmin || isSuperAdmin) && (
          <NavGroup label="Admin" icon={Shield}>
            <NavItem to="/branches" icon={Building2} label="Branches" testId="nav-branches" />
            <NavItem to="/employees" icon={UserCircle} label="Users" testId="nav-employees" />
            <NavItem to="/settings" icon={Settings} label="Settings" testId="nav-settings" />
            {isSuperAdmin && <NavItem to="/super-admin" icon={Building2} label="Super Admin" testId="nav-super-admin" />}
            {isSuperAdmin && <NavItem to="/platform-admin" icon={Shield} label="Platform Admin" testId="nav-platform-admin" />}
          </NavGroup>
        )}
      </nav>

      {/* User / Logout */}
      <div className="border-t border-purple-100 dark:border-purple-900/30 px-3 py-3">
        <div className="flex items-center justify-between">
          <div className="min-w-0">
            <p className="text-[13px] font-semibold text-gray-800 dark:text-gray-200 truncate">{user?.name}</p>
            <p className="text-[9px] text-purple-400 dark:text-purple-500 truncate uppercase tracking-[0.1em] font-medium">{user?.role}</p>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={() => setShowAccount(true)} className="p-2 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-lg transition-all duration-200" title="Change username / password" data-testid="change-account-btn">
              <KeyRound className="w-4 h-4 text-gray-400 dark:text-gray-500" />
            </button>
            <button onClick={handleLogout} className="p-2 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-lg transition-all duration-200" data-testid="logout-btn">
              <LogOut className="w-4 h-4 text-gray-400 dark:text-gray-500" />
            </button>
          </div>
        </div>
      </div>

      {showAccount && <AccountSettingsModal onClose={() => setShowAccount(false)} />}
    </aside>
  );
}

/* ─────────────────────────────────────────────────────────────────
 *  AccountSettingsModal — username + password change.
 *  Calls PATCH /auth/me with current_password verification.
 * ───────────────────────────────────────────────────────────────── */
function AccountSettingsModal({ onClose }) {
  const { user } = useAuth();
  const [username, setUsername] = useState(user?.username || '');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!currentPassword) return toast.error('Current password is required');
    if (newPassword && newPassword !== confirmPassword) return toast.error('New passwords do not match');
    if (newPassword && newPassword.length < 6) return toast.error('New password must be at least 6 characters');
    if (!newPassword && username === (user?.username || '')) return toast.error('Nothing to update');
    setSaving(true);
    try {
      const body = { current_password: currentPassword };
      if (username && username !== user?.username) body.username = username;
      if (newPassword) body.new_password = newPassword;
      const r = await authAPI.updateMe(body);
      toast.success(`Updated: ${(r.data?.fields_updated || []).join(', ') || 'account'}`);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-3" data-testid="account-settings-modal">
      <div className="bg-white dark:bg-slate-900 rounded-lg shadow-xl max-w-md w-full">
        <div className="border-b border-slate-200 dark:border-slate-700 p-4 flex items-center justify-between">
          <h3 className="font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2">
            <KeyRound className="w-4 h-4 text-purple-600" />
            Change Username / Password
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="text-xs font-medium text-slate-600 dark:text-slate-300">Username</label>
            <input className="form-input mt-1" value={username} onChange={e => setUsername(e.target.value)} data-testid="acct-username" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 dark:text-slate-300">Current password (required)</label>
            <input type="password" className="form-input mt-1" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} data-testid="acct-current-password" autoComplete="current-password" />
          </div>
          <div className="border-t border-slate-200 dark:border-slate-700 pt-3">
            <label className="text-xs font-medium text-slate-600 dark:text-slate-300">New password (leave blank to keep)</label>
            <input type="password" className="form-input mt-1" value={newPassword} onChange={e => setNewPassword(e.target.value)} data-testid="acct-new-password" autoComplete="new-password" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 dark:text-slate-300">Confirm new password</label>
            <input type="password" className="form-input mt-1" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} data-testid="acct-confirm-password" autoComplete="new-password" />
          </div>
          <div className="flex gap-2 pt-2">
            <button onClick={onClose} disabled={saving} className="flex-1 px-3 py-2 bg-slate-200 dark:bg-slate-800 text-slate-800 dark:text-slate-200 rounded text-sm hover:bg-slate-300 disabled:opacity-50">Cancel</button>
            <button onClick={submit} disabled={saving} className="flex-1 px-3 py-2 bg-purple-600 text-white rounded text-sm hover:bg-purple-700 disabled:opacity-50" data-testid="acct-save">
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
